import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { LogOut, ChevronDown, Menu, Globe, Check, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Separator } from './ui/separator';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from './ui/sheet';
import { useAuth } from '../context/AuthContext';
import alasLogo from '../assets/images/AlasLogo.png';

// Bayrak SVG bileşenleri (inline olarak tanımlanmış)
const TrFlag = () => (
  <svg width="50" height="32" viewBox="0 0 50 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-6 rounded-sm shadow-sm">
    <g clipPath="url(#clip0_tr)">
      <path d="M49.0528 32.4098H0.777147C0.301073 32.4098 -0.0849609 32.0237 -0.0849609 31.5477V0.513231C-0.0849609 0.0371571 0.301073 -0.348877 0.777147 -0.348877H49.0529C49.529 -0.348877 49.915 0.0371571 49.915 0.513231V31.5477C49.9149 32.0238 49.5289 32.4098 49.0528 32.4098Z" fill="#FF4B55"/>
      <path d="M12.8752 16.0303C12.8752 19.8418 15.9643 22.931 19.7724 22.931C21.1916 22.931 22.5003 22.4949 23.5903 21.7557C23.7554 21.6438 23.9462 21.8596 23.808 22.0035C22.2441 23.6324 20.0541 24.6511 17.6173 24.6511C12.6704 24.6511 8.69718 20.4809 9.01817 15.463C9.29854 11.0822 12.9306 7.56462 17.3179 7.41491C19.8716 7.32771 22.1747 8.35983 23.802 10.0517C23.9416 10.1968 23.7512 10.4159 23.5846 10.303C22.4957 9.5655 21.1891 9.12975 19.7724 9.12975C15.9644 9.12975 12.8752 12.2258 12.8752 16.0303Z" fill="#F5F5F5"/>
      <path d="M28.596 14.8443L27.2447 13.0766C27.1692 12.9778 27.0112 13.032 27.0124 13.1564L27.0321 15.3813L24.9334 16.1202C24.816 16.1615 24.8189 16.3284 24.9375 16.3658L27.0596 17.0346L27.1138 19.259C27.1168 19.3833 27.2766 19.4322 27.3487 19.3309L28.6405 17.5194L30.7728 18.1552C30.8919 18.1908 30.9878 18.0542 30.9138 17.9542L29.59 16.1657L30.8536 14.3344C30.9243 14.232 30.8239 14.0986 30.706 14.1381L28.596 14.8443Z" fill="#F5F5F5"/>
    </g>
    <defs>
      <clipPath id="clip0_tr">
        <rect width="50" height="32" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const EnFlag = () => (
  <svg width="49" height="32" viewBox="0 0 49 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-6 rounded-sm shadow-sm">
    <g clipPath="url(#clip0_en)">
      <path d="M48.6501 32.4097H0.374557C-0.101515 32.4097 -0.487549 32.0237 -0.487549 31.5476V0.513229C-0.487549 0.0371563 -0.101515 -0.348877 0.374557 -0.348877H48.6502C49.1263 -0.348877 49.5123 0.0371563 49.5123 0.513229V31.5476C49.5122 32.0237 49.1262 32.4097 48.6501 32.4097Z" fill="#41479B"/>
      <path d="M49.5123 0.513229C49.5123 0.0371563 49.1262 -0.348877 48.6501 -0.348877H44.7932L28.8226 10.1147V-0.348877H20.2019V10.1147L4.23138 -0.348877H0.374557C-0.101515 -0.348877 -0.487549 0.0371563 -0.487549 0.513229V2.74281L13.2144 11.72H-0.487549V20.3407H13.2144L-0.487549 29.318V31.5476C-0.487549 32.0237 -0.101515 32.4097 0.374557 32.4097H4.23148L20.2021 21.9462V32.4097H28.8228V21.9462L44.7934 32.4097H48.6503C49.1264 32.4097 49.5125 32.0237 49.5125 31.5476V29.318L35.8105 20.3408H49.5123V11.7201H35.8103L49.5123 2.74281V0.513229Z" fill="#F5F5F5"/>
      <path d="M49.5123 13.4442H27.0986V-0.348877H21.9261V13.4442H-0.487549V18.6166H21.9261V32.4097H27.0986V18.6166H49.5123V13.4442Z" fill="#FF4B55"/>
      <path d="M16.988 20.3408L-0.462891 31.6686C-0.401856 32.0849 -0.0585954 32.4098 0.374704 32.4098H1.5621L20.1548 20.3409H16.988V20.3408Z" fill="#FF4B55"/>
      <path d="M33.3399 20.3408H30.1731L48.7388 32.3919C49.1715 32.3458 49.513 31.9925 49.513 31.5476V30.8388L33.3399 20.3408Z" fill="#FF4B55"/>
      <path d="M-0.486816 1.46497L15.3119 11.7202H18.4786L0.0154278 -0.264709C-0.279493 -0.127698 -0.486816 0.166734 -0.486816 0.513314V1.46497Z" fill="#FF4B55"/>
      <path d="M31.9903 11.7203L49.4831 0.365134C49.4104 -0.0374038 49.0745 -0.348633 48.651 -0.348633H47.4159L28.8235 11.7203H31.9903Z" fill="#FF4B55"/>
    </g>
    <defs>
      <clipPath id="clip0_en">
        <rect width="49" height="32" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

/**
 * Header bileşeni - Sayfa üst kısmında logo ve kullanıcı menüsü içerir
 * @returns {JSX.Element} Header bileşeni
 */
const Header = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { logout } = useAuth();
  
  // Kullanıcı bilgilerini localStorage'dan al
  useEffect(() => {
    const sessionData = localStorage.getItem('alasfireSession');
    if (sessionData) {
      try {
        const session = JSON.parse(sessionData);
        if (session.dealer) {
          setUserName(session.dealer.dealer_name || session.dealer.username || '');
        } else if (session.user) {
          setUserName(session.user.email || '');
        }
      } catch (error) {
        console.error('Oturum bilgisi çözümlenirken hata:', error);
      }
    }
  }, []);
  
  // Çıkış yapma işlemi
  const handleLogout = async () => {
    try {
      // Auth context'in logout fonksiyonunu çağır
      const result = await logout();
      if (result.success) {
        // Kullanıcı adını temizle
        setUserName('');
        // Giriş sayfasına yönlendir
        navigate('/');
      }
    } catch (error) {
      console.error('Çıkış yaparken hata oluştu:', error);
    }
  };

  // Kullanıcı adının baş harfini al
  const getInitials = (name) => {
    return name ? name.charAt(0).toUpperCase() : 'U';
  };
  
  // Dil değiştirme işlemi
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    // Dil tercihini localStorage'a kaydet
    localStorage.setItem('language', lng);
  };
  
  // Mevcut dil bilgisi
  const currentLanguage = i18n.language;

  // Navigation handler
  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };
  
  return (
    <header className="bg-background border-b h-14 sm:h-16 w-full sticky top-0 z-50 shadow-sm backdrop-blur-sm bg-background/90">
      <div className="h-full container mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 flex items-center justify-between">
        {/* Logo ve başlık */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 sm:flex-none">
          <img 
            src={alasLogo} 
            alt="Alas Fire Logo" 
            className="h-8 sm:h-10 w-auto flex-shrink-0"
          />
          <div className="hidden sm:flex flex-col min-w-0">
            <h1 className="text-sm sm:text-base lg:text-lg font-bold text-foreground tracking-tight truncate">
              {t('app.title')}
            </h1>
            <p className="text-xs text-muted-foreground hidden sm:block truncate">
              {t('app.subtitle')}
            </p>
          </div>
        </div>
        
        {/* Desktop Navigation - Hidden on mobile/tablet */}
        <div className="hidden lg:flex items-center gap-4 xl:gap-6">
          <Button variant="ghost" className="text-sm font-medium px-3" onClick={() => navigate('/calculator')}>
            {t('header.calculator')}
          </Button>
          <Button variant="ghost" className="text-sm font-medium px-3" onClick={() => navigate('/products')}>
            {t('header.products')}
          </Button>
          <Button variant="ghost" className="text-sm font-medium px-3" onClick={() => navigate('/parameters')}>
            {t('header.parameters')}
          </Button>
        </div>
        
        {/* Right side controls */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Language Dropdown - Responsive sizing */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-8 sm:h-9 gap-1 sm:gap-2 border-primary/20 hover:bg-primary/10 hover:text-primary px-2 sm:px-3"
                    >
                      {currentLanguage === 'tr' ? <TrFlag /> : <EnFlag />}
                      <span className="font-medium text-xs sm:text-sm hidden sm:inline">
                        {currentLanguage === 'tr' ? 'TR' : 'EN'}
                      </span>
                      <span className="font-medium text-xs sm:text-sm hidden md:inline">
                        {currentLanguage === 'tr' ? 'Türkçe' : 'English'}
                      </span>
                      <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 opacity-70" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-[140px] sm:w-[180px]">
                    <DropdownMenuLabel className="text-center text-xs sm:text-sm">
                      {t('app.select_language')}
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => changeLanguage('tr')}
                      className="cursor-pointer flex items-center gap-2 py-2"
                    >
                      <TrFlag />
                      <span className="flex-1 text-xs sm:text-sm">Türkçe</span>
                      {currentLanguage === 'tr' && <Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => changeLanguage('en')}
                      className="cursor-pointer flex items-center gap-2 py-2"
                    >
                      <EnFlag />
                      <span className="flex-1 text-xs sm:text-sm">English</span>
                      {currentLanguage === 'en' && <Check className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                {t('app.language')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {/* User Menu - Desktop */}
          {userName && (
            <div className="hidden sm:block">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 sm:h-9 pl-2 pr-2 flex items-center gap-1 sm:gap-2 hover:bg-accent hover:text-accent-foreground rounded-full">
                    <Avatar className="h-6 w-6 sm:h-8 sm:w-8 border-2 border-primary/10">
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs sm:text-sm">
                        {getInitials(userName)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-xs sm:text-sm hidden md:block max-w-[100px] xl:max-w-[150px] truncate">
                      {userName}
                    </span>
                    <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 hidden md:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48 sm:w-56" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none truncate">{userName}</p>
                      <p className="text-xs leading-none text-muted-foreground">{t('header.dealer_account')}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{t('header.logout')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          
          {/* Login Button - If not logged in */}
          {!userName && (
            <Button variant="outline" size="sm" onClick={() => navigate('/')} className="text-xs sm:text-sm h-8 sm:h-9 px-2 sm:px-3">
              {t('header.login')}
            </Button>
          )}
          
          {/* Mobile Menu Button */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden h-8 w-8 sm:h-9 sm:w-9">
                <Menu className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[350px] p-0">
              <SheetHeader className="p-4 sm:p-6 border-b">
                <div className="flex items-center justify-between">
                  <SheetTitle className="text-left text-base sm:text-lg">
                    {t('app.title')}
                  </SheetTitle>
                </div>
                {userName && (
                  <div className="text-left mt-2">
                    <p className="text-sm font-medium text-foreground">{userName}</p>
                    <p className="text-xs text-muted-foreground">{t('header.dealer_account')}</p>
                  </div>
                )}
              </SheetHeader>
              
              {/* Mobile Navigation */}
              <div className="flex flex-col p-4 sm:p-6 space-y-2">
                <Button 
                  variant="ghost" 
                  className="justify-start text-base h-12" 
                  onClick={() => handleNavigation('/calculator')}
                >
                  {t('header.calculator')}
                </Button>
                <Button 
                  variant="ghost" 
                  className="justify-start text-base h-12" 
                  onClick={() => handleNavigation('/products')}
                >
                  {t('header.products')}
                </Button>
                <Button 
                  variant="ghost" 
                  className="justify-start text-base h-12" 
                  onClick={() => handleNavigation('/parameters')}
                >
                  {t('header.parameters')}
                </Button>
                
                <Separator className="my-4" />
                
                {userName ? (
                  <>
                    <Button 
                      variant="ghost" 
                      className="justify-start text-base h-12 text-destructive hover:text-destructive" 
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      {t('header.logout')}
                    </Button>
                  </>
                ) : (
                  <Button 
                    variant="default" 
                    className="justify-start text-base h-12" 
                    onClick={() => handleNavigation('/')}
                  >
                    {t('header.login')}
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header; 